import { LocalVideoTrack, Room } from 'twilio-video';
import { useEffect, useCallback } from 'react';
import { SELECTED_BACKGROUND_SETTINGS_KEY } from '../../../constants';
import {
    GaussianBlurBackgroundProcessor,
    VirtualBackgroundProcessor,
    ImageFit,
    isSupported,
} from '@twilio/video-processors';
import Default1 from '../../../images/_default1.png';
import Default2 from '../../../images/_default2.png';
import Default1Thumb from '../../../images/_default1.png';
import Default2Thumb from '../../../images/_default2.png';

import { Thumbnail } from '../../BackgroundSelectionDialog/BackgroundThumbnail/BackgroundThumbnail';
import { useLocalStorageState } from '../../../hooks/useLocalStorageState/useLocalStorageState';

export interface BackgroundSettings {
    type: Thumbnail;
    index?: number;
}

const imageNames: string[] = [
    'Background 1',
    'Background 2',
];

const images = [
    Default1Thumb,
    Default2Thumb,
];

const rawImagePaths = [
    Default1,
    Default2,
];

let imageElements = new Map();

const getImage = (index: number): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
        if (imageElements.has(index)) {
            return resolve(imageElements.get(index));
        }
        const img = new Image();
        img.onload = () => {
            imageElements.set(index, img);
            resolve(img);
        };
        img.onerror = reject;
        img.src = rawImagePaths[index];
    });
};

export const backgroundConfig = {
    imageNames,
    images,
};

const virtualBackgroundAssets = '/virtualbackground';
let blurProcessor: GaussianBlurBackgroundProcessor;
let virtualBackgroundProcessor: VirtualBackgroundProcessor;

const isMobile = (): boolean => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const checkBrowserCapabilities = (): boolean => {
    try {
        const canvas = document.createElement('canvas');
        const hasCanvas2D = !!canvas.getContext('2d');
        const hasWebGL2 = !!canvas.getContext('webgl2');
        return hasCanvas2D || hasWebGL2;
    } catch (e) {
        console.error('Erro ao verificar capacidades do navegador:', e);
        return false;
    }
};

// Verifica suporte estendido incluindo mobile
const isBackgroundSupported = (): boolean => {
    const officialSupport = isSupported;
    const mobile = isMobile();
    const hasCapabilities = checkBrowserCapabilities();

  // Desktop: usa verificação oficial do Twilio
    if (!mobile) {
        return officialSupport;
    }

  // Mobile: verifica capacidades manualmente
  // AVISO: Performance pode ser reduzida em dispositivos móveis
    if (mobile && hasCapabilities) {
        console.warn(
      '⚠️ Virtual backgrounds em dispositivos móveis podem afetar a performance da chamada. ' +
        'Considere usar resoluções menores ou desabilitar se notar problemas.'
        );
        return true;
    }

    return false;
};

export default function useBackgroundSettings(videoTrack: LocalVideoTrack | undefined, room?: Room | null) {
    const [backgroundSettings, setBackgroundSettings] = useLocalStorageState<BackgroundSettings>(
        SELECTED_BACKGROUND_SETTINGS_KEY,
        { type: 'none', index: 0 }
    );

    const removeProcessor = useCallback(() => {
        if (videoTrack && videoTrack.processor) {
            videoTrack.removeProcessor(videoTrack.processor);
        }
    }, [videoTrack]);

    const addProcessor = useCallback(
        (processor: GaussianBlurBackgroundProcessor | VirtualBackgroundProcessor) => {
            if (!videoTrack || videoTrack.processor === processor) {
                return;
            }
            removeProcessor();
            videoTrack.addProcessor(processor);
        },
        [videoTrack, removeProcessor]
    );

    useEffect(() => {
        const supported = isBackgroundSupported();

        if (!supported) {
            console.warn('Virtual background não é suportado neste navegador');
            return;
        }

        const handleProcessorChange = async () => {
            try {
                const mobile = isMobile();

        // Configurações otimizadas para mobile (menor blur = melhor performance)
                const blurConfig = {
                    assetsPath: virtualBackgroundAssets,
                    ...(mobile && {
                        maskBlurRadius: 3,
                        blurFilterRadius: 8,
                    }),
                };

                const virtualBgConfig = {
                    assetsPath: virtualBackgroundAssets,
                    backgroundImage: await getImage(0),
                    fitType: ImageFit.Cover,
                    ...(mobile && {
                        maskBlurRadius: 3,
                    }),
                };

                if (!blurProcessor) {
                    blurProcessor = new GaussianBlurBackgroundProcessor(blurConfig);
                    await blurProcessor.loadModel();
                }

                if (!virtualBackgroundProcessor) {
                    virtualBackgroundProcessor = new VirtualBackgroundProcessor(virtualBgConfig);
                    await virtualBackgroundProcessor.loadModel();
                }

                if (backgroundSettings.type === 'blur') {
                    addProcessor(blurProcessor);
                } else if (backgroundSettings.type === 'image' && typeof backgroundSettings.index === 'number') {
                    virtualBackgroundProcessor.backgroundImage = await getImage(backgroundSettings.index);
                    addProcessor(virtualBackgroundProcessor);
                } else {
                    removeProcessor();
                }
            } catch (error) {
                console.error('Erro ao aplicar virtual background:', error);
                removeProcessor();
            }
        };

        handleProcessorChange();
    }, [backgroundSettings, videoTrack, room, addProcessor, removeProcessor]);

    return [backgroundSettings, setBackgroundSettings] as const;
}
